import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { labelValueGridSx } from '~/components/Fields/LabeledValue';
import SectionLabel from '~/components/SectionLabel';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';

/**
 * Responsive column flow for PropertySection blocks: two columns in the
 * details dialog, stacking when the container is narrow.
 */
export function PropertySectionColumns({ children }: { children: ReactNode }) {
    return (
        <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(190px, 1fr))"
            gap="14px 28px"
        >
            {children}
        </Box>
    );
}

/**
 * Two-column grid of compact rows under a counted section eyebrow: the
 * trailing "objects that reference this" lists of the details panels.
 */
export function CountedRowGrid({
    id,
    count,
    footer,
    children,
    'data-testid': testId,
}: {
    id: string;
    count: number;
    footer?: ReactNode;
    children: ReactNode;
    'data-testid'?: string;
}) {
    return (
        <Box mt={2} data-testid={testId}>
            {/* Section eyebrow with the list's item count beside it. */}
            <Box display="flex" alignItems="center" gap={0.5}>
                <SectionLabel id={id} />
                <Typography fontSize={10} color="text.disabled">
                    {count}
                </Typography>
            </Box>
            <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap="4px 16px"
                mt={0.75}
            >
                {children}
            </Box>
            {footer}
        </Box>
    );
}

/** One CountedRowGrid row: adornment, name taking the slack, trailing chip. */
export function CompactRow({
    leading,
    trailing,
    sensitive = false,
    children,
}: {
    leading?: ReactNode;
    trailing?: ReactNode;
    /** Mask the name while sensitive info is hidden. */
    sensitive?: boolean;
    children: ReactNode;
}) {
    return (
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            {leading}
            <Typography fontSize={11} noWrap flex={1} minWidth={0}>
                {sensitive ? (
                    <SensitiveInfoTypography>
                        {children}
                    </SensitiveInfoTypography>
                ) : (
                    children
                )}
            </Typography>
            {trailing}
        </Box>
    );
}

/** One titled block in the section flow of a details body. */
export default function PropertySection({
    id,
    children,
}: {
    id: string;
    children: ReactNode;
}) {
    return (
        <Box minWidth={0}>
            <SectionLabel id={id} />
            <Box sx={{ ...labelValueGridSx, columnGap: '10px', mt: 0.75 }}>
                {children}
            </Box>
        </Box>
    );
}
